import { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

const InstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Don't show if already installed (running in standalone mode)
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
      return;
    }

    // Don't show if user previously installed or permanently dismissed
    const installed = localStorage.getItem('pwa-installed');
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (installed || dismissed) return;

    // Capture the browser's install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Also show banner after 3 seconds even without the event (for iOS/Safari)
    const timer = setTimeout(() => {
      if (!isInstalled && !installed && !dismissed) {
        setShowBanner(true);
      }
    }, 3000);

    const installedHandler = () => {
      setIsInstalled(true);
      setShowBanner(false);
      localStorage.setItem('pwa-installed', 'true');
    };
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
      clearTimeout(timer);
    };
  }, [isInstalled]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
        setShowBanner(false);
        localStorage.setItem('pwa-installed', 'true');
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: redirect to install page with instructions
      window.location.href = '/install';
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', 'true');
  };

  if (!showBanner || isInstalled) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px] z-[9999] animate-slide-up">
      <div className="bg-[#111111] border border-cyan-500/20 rounded-xl shadow-2xl shadow-cyan-500/10 p-4 backdrop-blur-xl">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-500/20">
            <Download className="w-6 h-6 text-white" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-bold text-white">Install Imago</h4>
              <button
                onClick={handleDismiss}
                className="text-gray-500 hover:text-gray-300 transition-colors p-1 -mr-1"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">
              Get the app experience — faster loading, native feel, no app store needed.
            </p>

            {/* Action buttons */}
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={handleInstall}
                className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-cyan-400 to-cyan-300 text-black text-xs font-bold font-mono rounded-lg hover:from-cyan-300 hover:to-cyan-200 transition-all shadow-md shadow-cyan-500/20"
              >
                <Download className="w-3.5 h-3.5" />
                Install
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 text-xs text-gray-400 hover:text-white transition-colors font-mono"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default InstallBanner;
